import axios, { AxiosRequestConfig, ResponseType } from "axios";
import { store } from "@/components/state/store";
import { t } from "i18next";
import {
  startLoading,
  stopLoading,
} from "@/components/state/general/loadingSlice";
import {
  isRetryableGatewayStatus,
  shouldRetryGatewayRequest,
  waitForGatewayRetry,
} from "@/helper/gatewayRetry";

type UrlParameters = {
  entity?: string;
  method: AxiosRequestConfig["method"];
  responseType?: ResponseType;
  successHandler?: any;
  failHandler?: any;
  entityId?: any;
  submitData?: any;
  config?: any;
  additionalRouteParts?: object;
  getAllPages?: boolean;
  shouldNotImpersonate?: boolean;
  disableGlobalSpinner?: boolean;
  skipGatewayRetry?: boolean;
  parametersToRender?: {
    additionalUrlParameters?: object;
    depth?: string;
    ordering?: any;
    limit?: any;
    offset?: any;
    search?: any;
    [key: string]: any;
  };
};

const baseURL = "/api/v1/";
const oauthRequiredHeader = "x-kaer-oauth-required";
const silentGatewayErrorEntities: ReadonlySet<string> = new Set(["me"]);

type RequestHeaders =
  | Record<string, unknown>
  | { get: (name: string) => unknown }
  | undefined;

export const isOAuthAuthenticationRequired = (headers: RequestHeaders) => {
  if (!headers) return false;
  if ("get" in headers && typeof headers.get === "function") {
    return String(headers.get(oauthRequiredHeader)) === "1";
  }
  return (
    String((headers as Record<string, unknown>)[oauthRequiredHeader]) === "1"
  );
};

const navigateToOAuthSignIn = () => {
  const returnUrl = encodeURIComponent(window.location.href);
  window.location.assign(`/oauth2/sign_in?rd=${returnUrl}`);
};

const genericRequest = (
  parameters: UrlParameters & { oauthRequiredHandler?: () => void }
) => {
  let requestURL = baseURL + parameters.entity + "/";
  const selectedChildCompany = store.getState().parent.subjectId;

  let parametersToRender: any = {};
  let additionalRouteParts: any = {};

  if (parameters.parametersToRender) {
    parametersToRender = parameters.parametersToRender;
  }
  if (parameters.additionalRouteParts) {
    additionalRouteParts = parameters.additionalRouteParts;
  }

  if (parameters.entityId) {
    requestURL += parameters.entityId + "/";
  }

  if (selectedChildCompany !== "" && !parameters?.shouldNotImpersonate) {
    parametersToRender["impersonate_subject"] = selectedChildCompany;
  }

  if (parametersToRender.additionalUrlParameters) {
    const { additionalUrlParameters, ...rest } = parametersToRender;
    const finalObject = { ...additionalUrlParameters, ...rest };
    parametersToRender = finalObject;
  }

  let routeParts: any = "";
  for (const key in additionalRouteParts) {
    routeParts += key + additionalRouteParts[key];
  }

  let renderedParameters: any = "";
  for (const key in parametersToRender) {
    if (renderedParameters === "") {
      renderedParameters += "?";
    } else {
      renderedParameters += "&";
    }

    renderedParameters += key + "=" + parametersToRender?.[key];
  }

  requestURL += routeParts + renderedParameters;
  doApiRequest(parameters, requestURL);
};

const parseBlobErrorResponseData = async (
  errorData: unknown,
): Promise<unknown> => {
  if (!(errorData instanceof Blob)) {
    return errorData;
  }

  try {
    const text = await errorData.text();
    if (!text) {
      return errorData;
    }

    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  } catch {
    return errorData;
  }
};

const getFailHandlerMessage = (errorData: unknown, status: any): any => {
  if (
    typeof errorData === "object" &&
    errorData !== null &&
    !Array.isArray(errorData) &&
    Object.keys(errorData).length > 0 &&
    status !== 500
  ) {
    return Object.entries(errorData).reduce(
      (acc, [field, msg]) => ({ ...acc, [field]: msg }),
      {},
    );
  }

  if (typeof errorData === "string" && status !== 500) {
    return errorData;
  }

  return t("generic_error_message", { error: status });
};

const buildApiRequestErrorMessage = async (
  error: any,
  parameters: any,
): Promise<{ message: any; status: any; code: any }> => {
  let message: any = error;
  const status: any = error?.response?.status ?? "";
  const code = error?.code;

  if (String(status) === "413") {
    message = t("upload_file_too_large");
  } else if (error.response?.data) {
    let errorData = error.response.data;
    if (parameters.responseType === "blob") {
      errorData = await parseBlobErrorResponseData(errorData);
    }
    message = getFailHandlerMessage(errorData, status);
  }

  return { message, status, code };
};

const doApiRequest = (
  parameters: any,
  requestURL: string,
  previousData?: any,
  retryCount = 0,
): Promise<void> => {
  return axios({
    method: parameters.method,
    url: requestURL,
    data: parameters.submitData,
    withCredentials: true,
    ...(parameters.config || {}),
    ...(parameters.responseType && { responseType: parameters.responseType }),
  })
    .then((res) => {
      if ([200, 201, 202, 204, 207].includes(res.status)) {
        if (parameters.getAllPages === true) {
          if (previousData) {
            res.data.results = res.data.results.concat(previousData);
          }

          if (res.data.next && res.data.next !== null) {
            return doApiRequest(parameters, res.data.next, res.data.results);
          }
        }
        parameters.successHandler?.(res);
      }
    })
    .catch(async (error: any) => {
      let message: any = error;
      let status: any = error?.response?.status ?? "";
      let code: any = error?.code;

      if (isOAuthAuthenticationRequired(error?.response?.headers)) {
        parameters.oauthRequiredHandler?.();
        return;
      }

      if (
        !parameters.skipGatewayRetry &&
        shouldRetryGatewayRequest(parameters.method, status, retryCount)
      ) {
        await waitForGatewayRetry();
        return doApiRequest(
          parameters,
          requestURL,
          previousData,
          retryCount + 1,
        );
      }

      try {
        ({ message, status, code } = await buildApiRequestErrorMessage(
          error,
          parameters,
        ));
      } catch {
        message = t("generic_error_message", { error: status });
      } finally {
        const shouldSuppressMessage =
          silentGatewayErrorEntities.has(parameters.entity ?? "") &&
          isRetryableGatewayStatus(status);
        parameters.failHandler?.(
          shouldSuppressMessage ? undefined : message,
          status,
          code,
        );
      }
    });
};
//---------------------------------------------------------------------------------------------------------
export type ApiRequestParams = Omit<
  UrlParameters,
  "successHandler" | "failHandler"
>;

export interface ApiRequestFailureMeta {
  message: any;
  status: any;
  code: any;
}

/**
 * Promise variant of api.genericApiRequest.
 *
 * Resolves with the same response object successHandler receives. Rejects with
 * the SHAPED failure message (field-keyed object | string | i18n fallback),
 * exactly what failHandler receives, so the rejection value can be passed
 * unchanged to setError / GenericErrorMessageModal.
 *
 * Preserved quirk: fulfilled responses with a status outside
 * [200, 201, 202, 204, 207] invoke neither handler, so the promise never
 * settles — identical to the hand-rolled `new Promise` wraps this replaces.
 * Fixing that is a separate decision.
 */
export const apiRequest = <T = any,>(
  parameters: ApiRequestParams,
): Promise<{ data: T } & Record<string, any>> =>
  new Promise((resolve, reject) => {
    api.genericApiRequest({
      ...parameters,
      successHandler: resolve,
      failHandler: (message: any) => reject(message),
    });
  });

/**
 * Like apiRequest, but rejects with { message, status, code } for consumers
 * that need the http status or error code (e.g. conflict handling).
 */
export const apiRequestWithMeta = <T = any,>(
  parameters: ApiRequestParams,
): Promise<{ data: T } & Record<string, any>> =>
  new Promise((resolve, reject) => {
    api.genericApiRequest({
      ...parameters,
      successHandler: resolve,
      failHandler: (message: any, status: any, code: any) =>
        reject({ message, status, code } satisfies ApiRequestFailureMeta),
    });
  });

export class api {
  public static genericApiRequest = (parameters: UrlParameters) => {
    if (!parameters.disableGlobalSpinner) {
      store.dispatch(startLoading());
    }

    genericRequest({
      entity: parameters.entity,
      shouldNotImpersonate: parameters.shouldNotImpersonate,
      method: parameters.method,
      responseType: parameters.responseType,
      successHandler: (res: any) => {
        parameters.successHandler?.(res);
        if (!parameters.disableGlobalSpinner) {
          store.dispatch(stopLoading());
        }
      },
      failHandler: (msg: any, status: any, code: any) => {
        parameters.failHandler?.(msg, status, code);
        if (!parameters.disableGlobalSpinner) {
          store.dispatch(stopLoading());
        }
      },
      entityId: parameters.entityId,
      submitData: parameters.submitData,
      config: parameters.config,
      skipGatewayRetry: parameters.skipGatewayRetry,
      parametersToRender: parameters.parametersToRender,
      additionalRouteParts: parameters.additionalRouteParts,
      getAllPages: parameters.getAllPages,
      oauthRequiredHandler: () => {
        if (!parameters.disableGlobalSpinner) {
          store.dispatch(stopLoading());
        }
        navigateToOAuthSignIn();
      },
    });
  };
}
